/*
 * What kind of work the register is made of, for the type tile.
 *
 * Pure, in the shape of workOrderPriorityCounts.js, so the counting can be tested without
 * rendering a tile.
 *
 * Counted in the client for the same reason the priority tile is: the orders are already here,
 * and a count endpoint would be a second truth to keep in step with the list.
 *
 * The tile beside this one counts only what is open. This one counts finished and closed orders
 * too, which is a deliberate difference: "how many of these are preventive maintenance" is a
 * question about what the department spends its time on, not about what is on fire right now, and
 * a distribution that forgot every job as it was completed would answer neither. Drafts are the
 * one exclusion — a fault not yet raised is not work anybody has done or will do, the same reason
 * isWorkOrderOpen leaves them out.
 */
import { WorkOrderStatus, WorkOrderType } from "./workOrderEnums";

/*
 * The order the tile reads in, most disruptive first. It lives here rather than in the component
 * so no future edit can reorder some JSX and put preventive maintenance ahead of a breakdown.
 */
const typeOrder = [
  WorkOrderType.MachineBreakdown,
  WorkOrderType.MachineProblem,
  WorkOrderType.PreventiveMaintenance,
];

/*
 * Raised orders per type as [{ workOrderType, count }], in typeOrder.
 *
 * Types with nothing on them are left out rather than reported as zero: the tile renders what it
 * is given, and a strip reading "0 Breakdown 0 Problem" is height spent saying nothing happened.
 * An empty register therefore returns [], which is what makes the tile disappear instead of
 * sitting there empty.
 */
export const getWorkOrderTypeCounts = (workOrders) => {
  const counts = new Map();

  (workOrders ?? []).forEach((workOrder) => {
    if (workOrder?.status === WorkOrderStatus.Draft) return;
    // A missing row leaves an undefined key, which typeOrder then filters back out — the same
    // way an unknown type is dropped for having nowhere to be shown.
    const workOrderType = workOrder?.workOrderType;
    counts.set(workOrderType, (counts.get(workOrderType) ?? 0) + 1);
  });

  return typeOrder
    .filter((workOrderType) => counts.has(workOrderType))
    .map((workOrderType) => ({ workOrderType, count: counts.get(workOrderType) }));
};
